import { gsap } from 'gsap'
import { focusQuietly, trackInputModality } from '../lib/quietFocus'

const DRAWER_DURATION = 0.45
const DRAWER_EASE = 'power3.out'
const LINK_DURATION = 0.35
const LINK_STAGGER = 0.07
const LINK_OFFSET = 30
const SCROLL_DURATION = 0.3
const SCROLL_THRESHOLD = 80
const MOBILE_BREAKPOINT = 991

export default function NavScroll() {
  const navbar = document.querySelector<HTMLElement>('[ck-navbar]')
  if (!navbar) return
  const btn = document.querySelector<HTMLElement>('[ck-navbar-btn]')
  const drawer = document.querySelector<HTMLElement>('[ck-navbar-drawer]')
  if (!btn || !drawer) return

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
  const durDrawer = reduced ? 0 : DRAWER_DURATION
  const durLink = reduced ? 0 : LINK_DURATION
  const stgLink = reduced ? 0 : LINK_STAGGER
  const durScroll = reduced ? 0 : SCROLL_DURATION
  // Everything beside the navbar goes inert while the drawer is open, except the share toast:
  // it has to stay announceable when the drawer's own Share button is used.
  const siblings = [...navbar.parentElement!.children].filter(
    (el) => el !== navbar && el !== drawer && !el.hasAttribute('ck-share-toast')
  )

  const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT

  // Focus still moves into and out of the drawer for everyone, but only keyboard users see a ring
  trackInputModality()

  // ── Sticky ticker ──
  // The ticker is always fixed to the top of the viewport. While the navbar is visible it sits
  // directly below it; when the navbar slides away on scroll-down the ticker slides up to take
  // its place, and comes back down together with the navbar on scroll-up.
  const ticker = document.querySelector<HTMLElement>('[ck-ticker]')
  const navHeight = () => navbar.offsetHeight
  const placeTicker = () => {
    if (ticker) gsap.set(ticker, { top: navHeight() })
  }
  placeTicker()
  addEventListener('resize', placeTicker, { passive: true })
  document.fonts?.ready.then(placeTicker)

  const setNavVisible = (visible: boolean) => {
    gsap.to(navbar, { y: visible ? 0 : '-100%', duration: durScroll, overwrite: true })
    if (ticker) gsap.to(ticker, { y: visible ? 0 : -navHeight(), duration: durScroll, overwrite: true })
  }

  // ── Drawer setup ──
  drawer.classList.remove('is-open')
  drawer.removeAttribute('data-state')
  gsap.set(drawer, { xPercent: 100, autoAlpha: 0 })

  const links = drawer.querySelectorAll('[ck-navbar-link]')
  gsap.set(links, { x: LINK_OFFSET, autoAlpha: 0 })

  const tl = gsap.timeline({ paused: true })
  tl.to(drawer, { xPercent: 0, autoAlpha: 1, duration: durDrawer, ease: DRAWER_EASE }).to(
    links,
    { x: 0, autoAlpha: 1, duration: durLink, stagger: stgLink, ease: 'power2.out' },
    '-=0.15'
  )

  const focusable = () => [
    ...drawer.querySelectorAll<HTMLElement>(
      'a[href], button, [tabindex]:not([tabindex="-1"])'
    ),
  ]

  const isOpen = () => btn.getAttribute('aria-expanded') === 'true'

  const open = () => {
    if (!isMobile()) return
    btn.setAttribute('aria-expanded', 'true')
    drawer.setAttribute('aria-hidden', 'false')
    btn.textContent = 'Close'
    document.documentElement.classList.add('drawer-open')
    siblings.forEach((el) => el.setAttribute('inert', ''))
    tl.restart()
    const items = focusable()
    if (items.length) {
      tl.eventCallback('onComplete', () => {
        focusQuietly(items[0])
        tl.eventCallback('onComplete', null)
      })
    }
  }

  const close = (returnFocus = true) => {
    btn.setAttribute('aria-expanded', 'false')
    drawer.setAttribute('aria-hidden', 'true')
    btn.textContent = 'Menu'
    document.documentElement.classList.remove('drawer-open')
    siblings.forEach((el) => el.removeAttribute('inert'))
    tl.reverse()
    if (returnFocus) focusQuietly(btn)
  }

  // ── Drawer events ──

  btn.addEventListener('click', (e) => {
    e.preventDefault()
    if (!isMobile()) return
    isOpen() ? close() : open()
  })

  btn.addEventListener('keydown', (e) => {
    if (e.key === ' ') {
      e.preventDefault()
      btn.click()
    }
  })

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) close()
  })

  document.addEventListener('click', (e) => {
    if (!isOpen()) return
    if (drawer.contains(e.target as Node) || btn.contains(e.target as Node)) return
    close()
  })

  drawer.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null
    if (!link || !isOpen()) return
    // The Share button is an action, not navigation: keep the drawer open so its
    // "link copied" message shows up right where the tap happened.
    if (link.hasAttribute('ck-share')) return
    const href = link.getAttribute('href')
    if (href?.startsWith('#')) {
      close(false)
      return
    }
    try {
      const url = new URL(link.href, location.href)
      if (url.origin === location.origin && url.pathname === location.pathname) close(false)
    } catch { }
  })

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || !isOpen()) return
    const ring = [btn, ...focusable()]
    const i = ring.indexOf(document.activeElement as HTMLElement)
    if (i < 0) return
    if (e.shiftKey && i === 0) {
      e.preventDefault()
      ring[ring.length - 1].focus()
    } else if (!e.shiftKey && i === ring.length - 1) {
      e.preventDefault()
      ring[0].focus()
    }
  })

  window.addEventListener('resize', () => {
    if (!isMobile() && isOpen()) close(false)
  })

  // ── Scroll hide / show ──

  let lastY = scrollY
  let raf = false

  const tick = () => {
    const y = scrollY
    if (!isOpen()) {
      if (y <= 0) setNavVisible(true)
      else if (y > lastY && y > SCROLL_THRESHOLD) setNavVisible(false)
      else if (y < lastY) setNavVisible(true)
    }
    lastY = y
    raf = false
  }

  addEventListener(
    'scroll',
    () => {
      if (!raf) {
        requestAnimationFrame(tick)
        raf = true
      }
    },
    { passive: true }
  )
}
